import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const BoardFormDialog = ({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  mode = "create",
  initialValues = { name: "", description: "" },
}) => {
  const { t } = useTranslation("teamTasks");
  const isEdit = mode === "edit";
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(initialValues.name || "");
    setDescription(initialValues.description || "");
  }, [open, initialValues.name, initialValues.description]);

  const handleClose = () => {
    setName("");
    setDescription("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim() || isLoading) return;
    const result = await onSubmit({
      name: name.trim(),
      description: description.trim() || null,
    });
    if (result === false) return;
    if (!isEdit) {
      setName("");
      setDescription("");
    }
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {isEdit ? t("editBoard") : t("createBoard")}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {isEdit ? t("editBoardIntro") : t("createBoardIntro")}
        </Typography>
        <TextField
          label={t("boardName")}
          placeholder={t("boardNamePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          fullWidth
          onKeyDown={(e) => {
            if (e.key === "Enter" && name.trim() && !isLoading) handleSubmit();
          }}
        />
        <TextField
          label={t("boardDescription")}
          placeholder={t("boardDescriptionPlaceholder")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
        {!isEdit && (
          <Box sx={{ py: 1.25, px: 1.5, borderRadius: 1.5, bgcolor: "action.hover" }}>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
              {t("createBoardHint")}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isLoading ? t("saving") : isEdit ? t("saveChanges") : t("createBoard")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoardFormDialog;
